import { api } from '@/shared/api/client'

/**
 * The Dossier client — `/api/v1/intel`.
 *
 * ## Three things every shape here is trying to make impossible to ignore
 *
 * **The grade travels with the row.** `sourceReliability` and `infoCredibility`
 * are on `Item`, not filtered away on the server and forgotten. The whole point
 * of the Admiralty pair is that a reader can tell a `D5` tweet from an `A1`
 * judgment, and a client that dropped the columns would render them identically.
 *
 * **Two dates, never one.** `occurredAt` is when the thing happened;
 * `collectedAt` is when this platform took custody. They answer different
 * questions — "was this known before nominations closed" needs the second — and
 * `occurredAt` is nullable because an unknown event date is recorded as unknown
 * rather than guessed.
 *
 * **A subject is not a person.** `subjectRef` and `slug` are both nullable and
 * exactly one is set: a `TOPIC` like "the Nepal landslide" owns no row anywhere,
 * and the client must not assume a name belongs to somebody.
 */

// ── Subjects ────────────────────────────────────────────────────────────────

export interface Subject {
  id: string
  subjectTypeCode: string
  subjectRef: string | null
  slug: string | null
  displayName: string
  scopeUnitId: string | null
  scopeUnitPath: string | null
  statusCode: string
  sensitivityCode: string
  legalBasisCode: string
  watchlist: boolean
  /** The act that starts surveillance. On the list row on purpose. */
  collectionEnabled: boolean
  collectionPurpose: string | null
  publicFigure: boolean
  publicFigureBasisCode: string | null
  publicFigureNote: string | null
  itemCount: number
  firstSeenAt: string | null
  lastActivityAt: string | null
  retentionUntil: string | null
  mergedIntoId: string | null
  version: number
}

export interface Identifier {
  id: string
  kindCode: string
  platformCode: string | null
  /** PII kinds arrive MASKED. There is no endpoint that unmasks them. */
  value: string
  verified: boolean
  confidence: number
}

export interface SubjectDetail {
  subject: Subject
  identifiers: Identifier[]
  tags: string[]
}

export interface ResolvedSubject {
  subjectId: string
  subjectTypeCode: string
  displayName: string
  /** True when this call OPENED the file rather than finding it. */
  created: boolean
}

// ── Items ───────────────────────────────────────────────────────────────────

export interface Item {
  id: string
  subjectId: string
  itemTypeCode: string
  categoryCode: string
  title: string | null
  summary: string | null
  body: string | null
  url: string | null
  /** When it happened. NULL when unknown — never guessed. */
  occurredAt: string | null
  publishedAt: string | null
  /** When THIS platform took custody. Never null, never backdated. */
  collectedAt: string
  collectionMethodCode: string
  originCode: string
  sourceId: string
  sourceReliability: string
  infoCredibility: string
  /** The pair, pre-joined: "B2". */
  grading: string
  veracityCode: string
  sensitivityCode: string
  legalBasisCode: string
  redacted: boolean
  redactedReason: string | null
  attestationText: string | null
  attestedBy: string | null
  attestedAt: string | null
  /** OCR output for an uploaded scan. Null until the sidecar has run. */
  extractedText: string | null
  ocrStatus: string | null
  /** The files the item ARRIVED with, as stored JSON. Attachments are separate. */
  mediaJson: string | null
  /** Structured fields for a typed item, as stored JSON. */
  payloadJson: string | null
  corroboratesItemId: string | null
  disputesItemId: string | null
  collectedBy: string | null
  collectionRunId: string | null
  externalId: string | null
  language: string | null
  retentionUntil: string | null
  version: number
}

/**
 * A supporting file on an item.
 *
 * Attachments are APPEND-ONLY. There is no delete anywhere in this client because there is
 * none on the server: a file that turns out to be doctored or misfiled is marked
 * `dependable: false` with a reason and stays visible with the mark, so a reader who saw it
 * cited last month can find out why not to rely on it.
 */
export interface Attachment {
  id: string
  itemId: string
  storageFileId: string
  filename: string
  contentType: string | null
  sizeBytes: number | null
  sha256: string
  caption: string | null
  ocrStatus: string | null
  dependable: boolean
  undependableReason: string | null
  markedBy: string | null
  markedAt: string | null
  attachedBy: string | null
  attachedAt: string
}

export interface ItemPage {
  content: Item[]
  nextCursor: string | null
  hasMore: boolean
  /**
   * How many rows the caller's authorities removed. Rendered, never swallowed:
   * a silently shorter list reads as "there is nothing there".
   */
  withheldSensitive: number
}

export interface CategoryBadge {
  code: string
  label: string
  itemCount: number
  attributeCount: number
  lastCollectedAt: string | null
  /** False renders the tab DISABLED rather than hiding it — see the file comment. */
  available: boolean
}

export interface Attribute {
  id: string
  namespace: string
  key: string
  label: string
  categoryCode: string | null
  dataTypeCode: string | null
  valueText: string | null
  valueNumber: number | null
  valueDate: string | null
  valueBool: boolean | null
  valueJson: string | null
  unit: string | null
  methodCode: string
  confidence: number
  veracityCode: string
  sensitivityCode: string
  legalBasisCode: string
  sourceItemId: string | null
  asOf: string
  validUntil: string | null
}

export interface Relationship {
  id: string
  fromSubjectId: string
  toSubjectId: string
  relationshipTypeCode: string
  roleLabel: string | null
  strength: number | null
  directed: boolean
  since: string | null
  until: string | null
  confidence: number
  veracityCode: string
  sourceItemId: string | null
  notes: string | null
}

export interface Assessment {
  id: string
  subjectId: string
  kindCode: string
  score: number | null
  band: string | null
  /** Mandatory on the server. An unexplained number gets quoted as a fact. */
  rationale: string
  breakdownJson: string | null
  inputsJson: string | null
  methodCode: string
  modelRef: string | null
  confidence: number
  assessedAt: string
  assessedBy: string | null
  validUntil: string | null
  supersedesId: string | null
  current: boolean
}

export interface Dossier {
  categoryCode: string | null
  label: string
  items: ItemPage
  attributes: Attribute[]
  relationships: Relationship[]
  assessments: Assessment[]
  /** Names what WOULD fill an empty tab. Not the same as "there is nothing". */
  emptyMessage: string | null
}

export interface Gaps {
  emptyCategoryCodes: string[]
  staleAttributeCount: number
  neverCollectedSourceCodes: string[]
  lastCollectedAt: string | null
}

export interface GraphNode {
  subjectId: string
  subjectTypeCode: string
  displayName: string
  depth: number
}

export interface GraphEdge {
  id: string
  fromSubjectId: string
  toSubjectId: string
  relationshipTypeCode: string
  roleLabel: string | null
  directed: boolean
  confidence: number
  veracityCode: string
}

export interface SubjectGraph {
  nodes: GraphNode[]
  edges: GraphEdge[]
  depth: number
  /** True when the node cap clipped it. Rendered — a clipped graph reads as complete. */
  truncated: boolean
}

// ── Sources, collection, governance ─────────────────────────────────────────

export interface Source {
  id: string
  code: string
  name: string
  kindCode: string
  /** Admiralty A–F, per PUBLICATION. Two feeds cannot share one grade. */
  reliability: string
  reliabilityNote: string | null
  integrationId: string | null
  baseUrl: string | null
  country: string | null
  language: string | null
  requestDelayMs: number | null
  active: boolean
  lastReviewedAt: string | null
  itemCount: number
  /** Which DRIVER fetches it. Null for a source only ever written to by hand. */
  driverCode: string | null
  fetchConfigJson: string | null
  defaultCredibility: string | null
  defaultItemType: string | null
  lastFetchAt: string | null
  lastFetchStatus: string | null
}

export interface ConfigKey {
  key: string
  label: string
  required: boolean
  example: string | null
}

/** A DRIVER, not a publication. Adding a newspaper needs no new driver. */
export interface Collector {
  code: string
  label: string
  supportedSourceKindCodes: string[]
  supportedSubjectTypeCodes: string[]
  accessModeCode: string
  legalBasisCode: string
  defaultCredibility: string
  requiresCredentials: boolean
  /** The "add a publication" form is generated from these. */
  configKeys: ConfigKey[]
}

export interface SourceTest {
  reachable: boolean
  statusCode: string
  robotsDecision: string | null
  itemsFound: number
  sampleTitles: string[]
  message: string | null
}

export interface Plan {
  id: string
  name: string
  description: string | null
  collectorCode: string
  sourceId: string
  subjectId: string | null
  paramsJson: string | null
  cadenceCode: string
  legalBasisCode: string
  /** Why this platform is collecting on this subject. Mandatory to enable. */
  purpose: string
  maxItemsPerRun: number
  lookbackDays: number | null
  enabled: boolean
  nextRunAt: string | null
  lastRunAt: string | null
  consecutiveFailures: number
  version: number
}

export interface Run {
  id: string
  planId: string | null
  collectorCode: string
  subjectId: string | null
  triggerCode: string
  /** SKIPPED_* statuses are things an operator FIXES, not errors to hide. */
  statusCode: string
  startedAt: string | null
  finishedAt: string | null
  itemsFound: number
  itemsNew: number
  itemsDuplicate: number
  itemsRejected: number
  httpRequests: number
  errorCode: string | null
  errorDetail: string | null
  robotsDecision: string | null
}

export interface Watch {
  id: string
  name: string
  description: string | null
  subjectId: string | null
  conditionJson: string
  severity: string
  channelsJson: string | null
  cooldownSeconds: number
  enabled: boolean
  lastFiredAt: string | null
  fireCount: number
}

export interface Alert {
  id: string
  watchId: string
  subjectId: string
  itemId: string | null
  severity: string
  title: string
  detail: string | null
  statusCode: string
  acknowledgedBy: string | null
  acknowledgedAt: string | null
  resolutionNote: string | null
  firedAt: string
}

export interface ImportRun {
  id: string
  filename: string
  formatCode: string
  statusCode: string
  rowsParsed: number
  rowsCreate: number
  rowsMatch: number
  rowsReject: number
  reportJson: string | null
  expiresAt: string
  appliedAt: string | null
}

export interface PurgeReport {
  examined: number
  deleted: number
  /** True is the SAFE outcome, not a failure. Above the sanity ceiling. */
  refused: boolean
  reason: string | null
  byItemType: Record<string, number>
}

export type Vocabulary = Record<string, { code: string; label: string }[]>

// ── Calls ───────────────────────────────────────────────────────────────────

const base = '/intel'

export const listSubjects = (params: Record<string, string | undefined> = {}) =>
  api.get<Subject[]>(`${base}/subjects${query(params)}`)

export const getSubject = (id: string) =>
  api.get<SubjectDetail>(`${base}/subjects/${id}`)

export const createSubject = (body: unknown) =>
  api.post<Subject>(`${base}/subjects`, body)

export const resolveSubject = (body: unknown) =>
  api.post<ResolvedSubject>(`${base}/subjects/resolve`, body)

export const setWatchlist = (id: string, on: boolean, reason?: string) =>
  api.post<Subject>(`${base}/subjects/${id}/watchlist`, { on, reason })

/** The act that starts surveillance. `purpose` is mandatory when enabling. */
export const setCollection = (id: string, enabled: boolean, purpose?: string) =>
  api.post<Subject>(`${base}/subjects/${id}/collection`, { enabled, purpose })

export const setPublicFigure = (id: string, publicFigure: boolean, basisCode?: string,
                                note?: string) =>
  api.post<Subject>(`${base}/subjects/${id}/public-figure`, { publicFigure, basisCode, note })

export const listCategories = (id: string) =>
  api.get<CategoryBadge[]>(`${base}/subjects/${id}/categories`)

export const getDossier = (id: string, params: Record<string, string | undefined> = {}) =>
  api.get<Dossier>(`${base}/subjects/${id}/dossier${query(params)}`)

export const getTimeline = (id: string, params: Record<string, string | undefined> = {}) =>
  api.get<ItemPage>(`${base}/subjects/${id}/timeline${query(params)}`)

export const getGaps = (id: string) => api.get<Gaps>(`${base}/subjects/${id}/gaps`)

export const getGraph = (id: string, depth = 1) =>
  api.get<SubjectGraph>(`${base}/subjects/${id}/graph?depth=${depth}`)

export const listItems = (params: Record<string, string | undefined> = {}) =>
  api.get<ItemPage>(`${base}/items${query(params)}`)

export const getItem = (id: string) => api.get<Item>(`${base}/items/${id}`)

export const listAttachments = (itemId: string) =>
  api.get<Attachment[]>(`${base}/items/${itemId}/attachments`)

/**
 * Multipart, and deliberately plural — dragging in four scans is one act, not four.
 *
 * Hand-rolled rather than going through `api.post` for the same reason `uploadImport` is:
 * the shared client JSON-stringifies every body and stamps `Content-Type: application/json`,
 * which is right for the rest of the API and fatal for a multipart boundary.
 */
export const attachFiles = async (
  itemId: string, files: File[], caption?: string,
): Promise<Attachment[]> => {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  if (caption?.trim()) form.append('caption', caption.trim())
  const { apiBase } = await import('@/config/runtime-config')
  const { getAccessToken } = await import('@/shared/auth/tokenStore')
  const res = await fetch(`${apiBase}/intel/items/${itemId}/attachments`, {
    method: 'POST',
    body: form,
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    credentials: 'include',
  })
  if (!res.ok) throw await res.json()
  return res.json() as Promise<Attachment[]>
}

/** What exists INSTEAD of a delete. A reason of at least 20 characters is required to unmark. */
export const markAttachment = (attachmentId: string, dependable: boolean, reason?: string) =>
  api.post<Attachment>(`${base}/attachments/${attachmentId}/dependable`,
    { dependable, reason })

export const ingestItem = (body: unknown) => api.post<Item>(`${base}/items`, body)

export const setVeracity = (id: string, body: unknown) =>
  api.post<Item>(`${base}/items/${id}/veracity`, body)

export const redactItem = (id: string, reason: string) =>
  api.post<Item>(`${base}/items/${id}/redact`, { reason })

export const listSources = () => api.get<Source[]>(`${base}/sources`)

export const createSource = (body: unknown) => api.post<Source>(`${base}/sources`, body)

export const configureSource = (id: string, body: unknown) =>
  api.post<Source>(`${base}/sources/${id}/configure`, body)

/** Dry run. Fetches once, writes nothing — the add-a-publication safety net. */
export const testSource = (id: string, terms?: string) =>
  api.post<SourceTest>(`${base}/sources/${id}/test${terms ? `?terms=${encodeURIComponent(terms)}` : ''}`)

export const changeReliability = (id: string, reliability: string, note: string) =>
  api.post<Source>(`${base}/sources/${id}/reliability`, { reliability, note })

export const listCollectors = () => api.get<Collector[]>(`${base}/collectors`)

export const listPlans = () => api.get<Plan[]>(`${base}/plans`)

export const createPlan = (body: unknown) => api.post<Plan>(`${base}/plans`, body)

export const setPlanEnabled = (id: string, enabled: boolean) =>
  api.post<Plan>(`${base}/plans/${id}/enabled`, { enabled })

export const runPlanNow = (id: string, subjectId?: string) =>
  api.post<Run>(`${base}/plans/${id}/run${subjectId ? `?subjectId=${subjectId}` : ''}`)

export const listRuns = (params: Record<string, string | undefined> = {}) =>
  api.get<Run[]>(`${base}/runs${query(params)}`)

export const listWatches = () => api.get<Watch[]>(`${base}/watches`)

export const createWatch = (body: unknown) => api.post<Watch>(`${base}/watches`, body)

export const listAlerts = (params: Record<string, string | undefined> = {}) =>
  api.get<Alert[]>(`${base}/alerts${query(params)}`)

export const acknowledgeAlert = (id: string, note?: string) =>
  api.post<Alert>(`${base}/alerts/${id}/acknowledge`, { statusCode: 'ACKNOWLEDGED', note })

export const closeAlert = (id: string, statusCode: string, note?: string) =>
  api.post<Alert>(`${base}/alerts/${id}/close`, { statusCode, note })

export const runPurge = (dryRun = true) =>
  api.post<PurgeReport>(`${base}/retention/purge?dryRun=${dryRun}`)

/** Multipart: the dry run writes NOTHING. Applying it is a second, explicit call. */
export const uploadImport = async (file: File, formatCode: string): Promise<ImportRun> => {
  const form = new FormData()
  form.append('file', file)
  const { apiBase } = await import('@/config/runtime-config')
  const { getAccessToken } = await import('@/shared/auth/tokenStore')
  const res = await fetch(`${apiBase}/intel/imports?formatCode=${formatCode}`, {
    method: 'POST',
    body: form,
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    credentials: 'include',
  })
  if (!res.ok) throw await res.json()
  return res.json() as Promise<ImportRun>
}

export const applyImport = (id: string) =>
  api.post<ImportRun>(`${base}/imports/${id}/apply`)

export const getVocabulary = () => api.get<Vocabulary>(`${base}/subjects/vocabulary`)

export const getTaxonomy = () =>
  api.get<{ itemTypeCode: string; categoryCode: string }[]>(`${base}/items/taxonomy`)

function query(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '')
  if (entries.length === 0) return ''
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
}

// ── the evidentiary assay ────────────────────────────────────────────────────

/**
 * The dossier console's payload — `GET /intel/assay`.
 *
 * ## Two shapes here carry rules the screen must not soften
 *
 * **`occurredAt` is nullable and `latencyMinutes` goes null with it.** An item with no event
 * date is not an item we found instantly; it is one whose delay cannot be measured. A client
 * that defaulted either to zero would draw the archive as faster than it is, which is exactly
 * the flattery this board exists to refuse.
 *
 * **`standing` is signed and it is already computed.** The floor is a conjunction, so the
 * distance is measured on whichever Admiralty axis is binding — never averaged. The number
 * arrives decided so the plate and the chronology cannot draw the rule in two places.
 */
export interface AssayFloor {
  /** Admiralty A–F: an item meets the floor at or before this letter. */
  reliability: string
  /** Admiralty 1–6: at or before this digit. */
  credibility: string
}

export interface AssayStanding {
  items: number
  atOrAboveFloor: number
  belowFloor: number
  /** No event date. NOT latency zero — see the note above. */
  undated: number
  redacted: number
  disputed: number
  debunked: number
  corroborated: number
  singleSource: number
  unverified: number
  sources: number
  subjects: number
  /** Files whose entire record comes from ONE publication. */
  singleSourceSubjects: number
  latencyP50Minutes: number
  latencyP90Minutes: number
  /** Rows this caller's authorities kept out of every number above. */
  withheldSensitive: number
}

export interface AssayPlateCell {
  reliability: string
  credibility: string
  items: number
  contested: number
  meetsFloor: boolean
  lastCollectedAt: string | null
}

export interface AssayLatencyBucket {
  code: 'H1' | 'D1' | 'D7' | 'D30' | 'OVER' | 'UNDATED'
  label: string
  items: number
}

export interface AssaySourceRow {
  id: string
  code: string
  name: string
  kindCode: string
  reliability: string
  items: number
  atOrAboveFloor: number
  contested: number
  lastCollectedAt: string | null
}

export interface AssayCategoryRow {
  code: string
  label: string
  items: number
  atOrAboveFloor: number
  firstCollectedAt: string | null
  lastCollectedAt: string | null
}

export interface AssaySubjectRow {
  id: string
  displayName: string
  subjectTypeCode: string
  items: number
  atOrAboveFloor: number
  sources: number
  publicFigure: boolean
  collectionEnabled: boolean
  lastCollectedAt: string | null
}

export interface AssayItem {
  id: string
  subjectId: string
  subjectName: string
  categoryCode: string
  itemTypeCode: string
  title: string | null
  reliability: string
  credibility: string
  veracityCode: string
  /** When it happened. Null is legitimate and common. */
  occurredAt: string | null
  /** When this platform took custody. Never null. */
  collectedAt: string
  /** The interval. Null when there is no event date — never zero. */
  latencyMinutes: number | null
  /** Signed Admiralty steps from the floor, on the binding axis. */
  standing: number
  sourceId: string
  sourceName: string
  redacted: boolean
  url: string | null
}

export interface AssayLink {
  fromItemId: string
  toItemId: string
  kind: 'CORROBORATES' | 'DISPUTES'
}

export interface Assay {
  generatedAt: string
  windowDays: number
  floor: AssayFloor
  /** True when `items` is a systematic sample rather than the whole window. */
  truncated: boolean
  itemTotal: number
  /** 1 when everything is drawn, otherwise "every Nth". */
  stride: number
  standing: AssayStanding
  plate: AssayPlateCell[]
  latency: AssayLatencyBucket[]
  sources: AssaySourceRow[]
  categories: AssayCategoryRow[]
  subjects: AssaySubjectRow[]
  items: AssayItem[]
  links: AssayLink[]
}

export interface AssayQuery {
  windowDays?: number
  subjectId?: string
  categoryCode?: string
  floorReliability?: string
  floorCredibility?: string
  limit?: number
}

export const getAssay = (p: AssayQuery = {}) =>
  api.get<Assay>(`${base}/assay${query({
    windowDays: p.windowDays == null ? undefined : String(p.windowDays),
    subjectId: p.subjectId,
    categoryCode: p.categoryCode,
    floorReliability: p.floorReliability,
    floorCredibility: p.floorCredibility,
    limit: p.limit == null ? undefined : String(p.limit),
  })}`)
